// src/encounter/care-plan-tab/care-plan-team.tsx
// The plan board's top two cards, as the demo draws them:
//  - the care team strip: one card per A2 team member, a left rule in their lane's hue, avatar, name (only the signed-in
//    user's own name is known: A2 carries no names, so the others read their role), "role · signs <lanes>", "x/y signed";
//  - "Needs you now": "Signed in as …", the viewer's unsigned items, and the other lanes' unsigned items running this week.
import { useState } from 'react';
import { User } from 'lucide-react';
import { Avatar } from '@scripsteam/scrips-signal-ds';
import { cn } from '@/lib/utils';
import type { CarePlanActivityView } from '../lib/care-plan-view';
import type { ActivityAction } from './use-activity-action';
import { activityActionFor, signsText, type TeamStripMember } from './care-plan-board';
import { CARE_PLAN_LANE_LABEL, CARE_PLAN_LANE_LOOK, CP_BODY_STRONG, CP_CARD_TITLE, CP_LABEL, CP_LINK_ACTION, CP_META, CP_WHITE_CARD, weeksText } from './care-plan-labels';
import { ActivityActionButton, ActivityStateLine, CarePlanStateGlyph, LaneIconTile } from './care-plan-marks';

export type CarePlanTeamState =
  | { kind: 'loading' }
  | { kind: 'error' }
  | { kind: 'unavailable' }
  | { kind: 'members'; members: TeamStripMember[] };

function countGlyph(signed: number, total: number): 'done' | 'half' | 'empty' {
  return total > 0 && signed === total ? 'done' : signed > 0 ? 'half' : 'empty';
}

/**
 * The strip's columns follow its member count and the card's width (a container query): 2 columns on a narrow card,
 * 3 from @2xl, and the full count (up to 6) only from @5xl. Literal class names, so the styles are generated.
 */
const TEAM_COLS: Record<number, string> = {
  1: 'grid-cols-1',
  2: 'grid-cols-2',
  3: 'grid-cols-2 @2xl:grid-cols-3',
  4: 'grid-cols-2 @2xl:grid-cols-3 @5xl:grid-cols-4',
  5: 'grid-cols-2 @2xl:grid-cols-3 @5xl:grid-cols-5',
  6: 'grid-cols-2 @2xl:grid-cols-3 @5xl:grid-cols-6',
};

export function CarePlanTeamStrip({ team }: { team: CarePlanTeamState }) {
  return (
    <section aria-label="Care team" className={cn(CP_WHITE_CARD, '@container')}>
      {team.kind === 'loading' ? <p className={cn(CP_META, 'm-0')}>Reading the care team…</p> : null}
      {team.kind === 'error' ? <p className={cn(CP_META, 'm-0')}>The care team could not be read.</p> : null}
      {team.kind === 'unavailable' ? <p className={cn(CP_META, 'm-0')}>This environment does not record the care team yet.</p> : null}
      {team.kind === 'members' && team.members.length === 0 ? <p className={cn(CP_META, 'm-0')}>No team members on this plan yet.</p> : null}
      {team.kind === 'members' && team.members.length > 0 ? (
        <ul className={cn('m-0 grid list-none gap-3 p-0', TEAM_COLS[Math.min(Math.max(team.members.length, 1), 6)])}>
          {team.members.map((m) => (
            <li
              key={m.key}
              data-team-member={m.key}
              className={cn('flex min-w-0 items-center gap-3 border-l-3 pl-3', m.lanes[0] ? CARE_PLAN_LANE_LOOK[m.lanes[0]].rule : 'border-border-card')}
            >
              <Avatar
                size="lg"
                name={m.name ?? undefined}
                alt={m.name ?? m.role}
                fallbackIcon={<User size={20} aria-hidden />}
                className="shrink-0"
              />
              <div className="min-w-0 flex-1">
                <div className={CP_BODY_STRONG}>{m.name ?? m.role}</div>
                <div className={CP_META}>{m.name ? `${m.role} · ${signsText(m.lanes)}` : signsText(m.lanes)}</div>
                <div data-role-count className={cn(CP_META, 'flex items-center gap-2 tabular-nums')}>
                  <CarePlanStateGlyph kind={countGlyph(m.signed, m.total)} />
                  <span>
                    {m.signed}/{m.total} signed
                  </span>
                </div>
              </div>
            </li>
          ))}
        </ul>
      ) : null}
    </section>
  );
}

const FOCUS_ROWS = 3;

interface FocusRowProps {
  /** The viewer's own group: a draft reads Review, an accepted item reads Sign (both two-step). Else: Open. */
  own: boolean;
  busy: boolean;
  onAction?: (a: CarePlanActivityView, kind: ActivityAction) => void;
  onOpen: (a: CarePlanActivityView) => void;
}

function FocusRow({ activity: a, own, busy, onAction, onOpen }: FocusRowProps & { activity: CarePlanActivityView }) {
  const kind = activityActionFor(a);
  const draft = a.status === 'ai-draft' || a.status === 'draft';
  return (
    <li data-focus-row={a.id} data-status={a.status} className="flex min-w-0 items-center gap-3 py-2">
      <LaneIconTile lane={a.lane} size="small" />
      <div className="flex min-w-0 flex-1 flex-col items-start">
        <span className={cn(CP_BODY_STRONG, 'max-w-full break-words')} title={a.title}>
          {a.title}
        </span>
        <span className={cn(CP_META, 'flex flex-wrap items-center gap-1')}>
          <ActivityStateLine activity={a} />
          {/* A chip (AI draft / Draft) stands apart; status words need the dot before the lane. */}
          {a.status === 'ai-draft' || a.status === 'draft' ? '' : '· '}
          {CARE_PLAN_LANE_LABEL[a.lane]} · {weeksText(a)}
        </span>
      </div>
      {own && onAction && kind ? (
        <ActivityActionButton kind={kind} busy={busy} onConfirm={() => onAction(a, kind)} />
      ) : (
        <button type="button" data-focus-action="open" className={CP_LINK_ACTION} onClick={() => onOpen(a)} aria-label={`${own && draft ? 'Review' : 'Open'} ${a.title}`}>
          {own && draft ? 'Review' : 'Open'}
        </button>
      )}
    </li>
  );
}

function FocusGroup({ group, title, empty, items, ...row }: Omit<FocusRowProps, 'own'> & { group: 'mine' | 'week'; title: string; empty: string; items: CarePlanActivityView[] }) {
  const [all, setAll] = useState(false);
  const shown = all ? items : items.slice(0, FOCUS_ROWS);
  return (
    <div data-focus-group={group} className="min-w-0">
      <div className={cn(CP_LABEL, 'flex items-baseline gap-2 text-ink-secondary')}>
        <span>{title}</span>
        <span data-focus-count className="tabular-nums text-ink-meta">
          {items.length}
        </span>
      </div>
      {items.length === 0 ? (
        <p className={cn(CP_META, 'm-0 mt-2')}>{empty}</p>
      ) : (
        <ul className="m-0 mt-1 list-none divide-y divide-surface-line p-0">
          {shown.map((a) => (
            <FocusRow key={a.id} activity={a} own={group === 'mine'} {...row} />
          ))}
        </ul>
      )}
      {items.length > FOCUS_ROWS ? (
        <button type="button" aria-expanded={all} className={cn(CP_LINK_ACTION, 'mt-1 px-1')} onClick={() => setAll((v) => !v)}>
          {all ? 'Show fewer' : `Show all ${items.length}`}
        </button>
      ) : null}
    </div>
  );
}

export function NeedsYouNowCard({
  viewerName,
  mineTitle,
  mine,
  week,
  thisWeek,
  ...row
}: Omit<FocusRowProps, 'own'> & {
  viewerName: string | null;
  /** null = the viewer owns no item on this plan: the group is left out (the demo shows it only for a lane owner). */
  mineTitle: string | null;
  mine: CarePlanActivityView[];
  /** null = the plan week is not known (no plan start): the week group is left out. */
  week: number | null;
  thisWeek: CarePlanActivityView[];
}) {
  return (
    <section aria-label="Needs you now" data-plan-focus className={CP_WHITE_CARD}>
      <header className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1">
        <h2 className={CP_CARD_TITLE}>Needs you now</h2>
        {viewerName ? <span className={CP_META}>Signed in as {viewerName}</span> : null}
      </header>
      <div className="@container mt-3">
        <div className="grid grid-cols-1 gap-x-8 gap-y-4 @2xl:grid-cols-2">
          {mineTitle !== null ? (
            <FocusGroup group="mine" title={mineTitle} empty="Nothing in your lanes waits for you." items={mine} {...row} />
          ) : null}
          {week !== null ? (
            <FocusGroup
              group="week"
              title={`Week ${week}${mineTitle !== null ? ', other lanes' : ''}`}
              empty="Nothing else runs this week."
              items={thisWeek}
              {...row}
            />
          ) : null}
          {mineTitle === null && week === null ? (
            <p className={cn(CP_META, 'm-0')}>Nothing in your lanes waits for you.</p>
          ) : null}
        </div>
      </div>
    </section>
  );
}
